/**
 * Crisp live-text helpers. Every UI Text is created through `uiText()` so its canvas resolution
 * tracks the current integer zoom (ART_DIRECTION: `resolution = zoom`) and is re-rendered on
 * settings:change / scale resize, and after the web fonts finish loading.
 */
import Phaser from 'phaser';
import { bus, Events } from '@/core/events';
import { FONT_BODY } from '@/core/config';
import { ensureFonts } from './fonts';
import { Css } from './theme';

type TextStyle = Phaser.Types.GameObjects.Text.TextStyle;

interface Registry { texts: Set<Phaser.GameObjects.Text>; zoom: number; dispose: () => void }
const registries = new WeakMap<Phaser.Scene, Registry>();

export function currentZoom(scene: Phaser.Scene): number {
  const z = scene.scale?.zoom ?? 1;
  return Math.max(1, Math.round(z));
}

function registryFor(scene: Phaser.Scene): Registry {
  let reg = registries.get(scene);
  if (reg) return reg;
  const texts = new Set<Phaser.GameObjects.Text>();
  const refresh = () => {
    const z = currentZoom(scene);
    const changed = z !== reg!.zoom;
    reg!.zoom = z;
    for (const t of texts) {
      if (!t.active) { texts.delete(t); continue; }
      if (changed) t.setResolution(z); else t.updateText();
    }
  };
  const onSettings = () => refresh();
  const onResize = () => refresh();
  bus.onTyped(Events.SETTINGS_CHANGE, onSettings);
  scene.scale.on(Phaser.Scale.Events.RESIZE, onResize);
  void ensureFonts().then(() => { if (registries.get(scene) === reg) refresh(); });
  const dispose = () => {
    bus.offTyped(Events.SETTINGS_CHANGE, onSettings);
    scene.scale.off(Phaser.Scale.Events.RESIZE, onResize);
    texts.clear();
    registries.delete(scene);
  };
  reg = { texts, zoom: currentZoom(scene), dispose };
  registries.set(scene, reg);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, dispose);
  scene.events.once(Phaser.Scenes.Events.DESTROY, dispose);
  return reg;
}

export interface UiTextOptions extends TextStyle { crisp?: boolean }

/** Creates a Text with the project body font, integer-zoom resolution and registry tracking. */
export function uiText(scene: Phaser.Scene, x: number, y: number, str: string | string[], style: UiTextOptions = {}): Phaser.GameObjects.Text {
  const reg = registryFor(scene);
  const { crisp: _crisp, ...rest } = style;
  const t = scene.add.text(Math.round(x), Math.round(y), str, {
    fontFamily: FONT_BODY,
    fontSize: '16px',
    color: Css.ink,
    resolution: reg.zoom,
    ...rest,
  });
  reg.texts.add(t);
  t.once(Phaser.GameObjects.Events.DESTROY, () => reg.texts.delete(t));
  return t;
}

/** Re-measures every tracked text of a scene (e.g. after a font swap). */
export function refreshSceneTexts(scene: Phaser.Scene): void {
  const reg = registries.get(scene);
  if (!reg) return;
  for (const t of reg.texts) if (t.active) t.updateText();
}

/** Pixel width measurer bound to a hidden Text with the given style (for CJK wrapping). */
export function makeMeasurer(scene: Phaser.Scene, style: TextStyle): { measure: (s: string) => number; destroy: () => void } {
  const probe = scene.add.text(-1000, -1000, 'M', { fontFamily: FONT_BODY, fontSize: '16px', ...style }).setVisible(false).setActive(false);
  const measure = (s: string): number => {
    probe.style.syncFont(probe.canvas, probe.context);
    return probe.context.measureText(s).width;
  };
  return { measure, destroy: () => probe.destroy() };
}
